import { Accordion } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import { AccordionItem } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/accordion-item/AccordionItem";
import classNames from "classnames";
import { Facet, FacetDetail } from "@/types/productListing";
import { slugify } from "@/utils/slugify";
import { FacetItem } from "../FacetItem/FacetItem";
import styles from "./FacetGroup.module.scss";
import { useCallback, useState, useEffect } from "react";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { FacetSearch } from "../FacetSearch/FacetSearch";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";
import { useFeatureFlags } from "@/flags/flags";

const SEARCH_LENGTH = 20;
const INITIAL_NUMBER_TO_SHOW = 8;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 10;

type FacetGroupProps = {
  dispatchFacetAnalytics: (
    eventType: string,
    facetName: string,
    facetDetails: FacetDetail,
  ) => void;
  facet: Facet;
  open?: boolean;
  position?: number;
};

export const FacetGroup = ({
  dispatchFacetAnalytics,
  facet,
  open = true,
  position = 1,
}: FacetGroupProps) => {
  const [isFacetListExpanded, setIsFacetListExpanded] = useState(false);
  const [facetSearchValue, setFacetSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(open);
  const [selectionAnnouncement, setSelectionAnnouncement] = useState("");

  useEffect(
    function resetFacetSearchValueOnFacetChange() {
      setFacetSearchValue("");
    },
    [facet.details],
  );

  const filteredFacetDetailsList = facet.details.filter((facetDetails) =>
    facetSearchValue
      ? facetDetails.label
          .toLocaleLowerCase()
          .includes(facetSearchValue.toLowerCase())
      : true,
  );

  const isShowMoreOrFewerVisible =
    filteredFacetDetailsList.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

  const numberOfOwnBrandItems = filteredFacetDetailsList
    .filter((facetDetails) =>
      facetSearchValue
        ? facetDetails.label
            .toLocaleLowerCase()
            .includes(facetSearchValue.toLowerCase())
        : true,
    )
    .filter(({ isBoosted }) => isBoosted === "true").length;

  const numberOfValuesToShow =
    isShowMoreOrFewerVisible && !isFacetListExpanded
      ? INITIAL_NUMBER_TO_SHOW
      : filteredFacetDetailsList.length;

  const showMoreOrFewerButton = useCallback(
    () => (
      <UnderlinedButton
        className={styles.facetListShowMore}
        onClick={() => {
          setIsFacetListExpanded(!isFacetListExpanded);
        }}
        data-testid={`facet-show-${isFacetListExpanded ? "fewer" : "more"}-button`}
        labelText={
          isFacetListExpanded
            ? "Show fewer"
            : `Show ${filteredFacetDetailsList.length - numberOfValuesToShow} more`
        }
      />
    ),
    [
      isFacetListExpanded,
      filteredFacetDetailsList.length,
      numberOfValuesToShow,
    ],
  );

  const isFacetListSearchable = facet.details.length > SEARCH_LENGTH;

  const selectedOptions = facet.details.filter(
    (detail) => detail.isSelected === "true",
  );

  const showSelectedFacetsSummary = selectedOptions.length > 0;

  const selectedFiltersSummaryForScreenReader = selectedOptions
    .map(({ label, qty }) => {
      const productLabel = qty === "1" ? "product" : "products";
      return `${label} (${qty} ${productLabel})`;
    })
    .join(", ");

  const selectedFiltersSummaryForDisplay = selectedOptions
    .map(({ label, qty }) => `${label} (${qty})`)
    .join(", ");

  // Wrapper function to announce selection changes for accessibility
  const dispatchFacetAnalyticsWithAnnouncement = useCallback(
    (eventType: string, facetName: string, facetDetails: FacetDetail) => {
      dispatchFacetAnalytics(eventType, facetName, facetDetails);

      // Announce the selection/deselection to screen readers
      const isSelected = facetDetails.isSelected === "true";
      const action =
        eventType === "filterRemoved" || !isSelected ? "removed" : "selected";
      setSelectionAnnouncement(
        `${facetDetails.label} ${action} in ${facetName} filter`,
      );
    },
    [dispatchFacetAnalytics],
  );

  return (
    <Accordion size="large">
      <AccordionItem
        className={styles.facetGroup}
        data-testid={`accordion-${facet.name}`}
        data-test2={`position${position}`}
        data-label={facet.name}
        onStateChange={(isOpen: boolean) => {
          setIsOpen(isOpen);
        }}
        header={
          <div className={styles.collapsedSummaryWrapper}>
            {/* Visually hidden announcement region for screen readers */}
            <span
              className={styles.visuallyHidden}
              aria-live="polite"
              role="status"
            >
              {selectionAnnouncement}
            </span>
            <h3 className={styles.facetLabel} id={slugify(facet.name)}>
              {facet.name}
              <span className={styles.visuallyHidden}> filter</span>
            </h3>
            {showSelectedFacetsSummary && (
              <p
                className={classNames(styles.selectedSummary, {
                  [styles.selectedSummary__hidden!]: isOpen,
                })}
              >
                <span className={styles.visuallyHidden}>
                  . Selected filters: {selectedFiltersSummaryForScreenReader}
                </span>
                <span aria-hidden="true">
                  {selectedFiltersSummaryForDisplay}
                </span>
              </p>
            )}
          </div>
        }
        isOpen={open}
      >
        {isFacetListSearchable && (
          <FacetSearch
            label={facet.name}
            facetSearchValue={facetSearchValue}
            setFacetSearchValue={setFacetSearchValue}
          />
        )}
        <ul className={styles.facetList}>
          {filteredFacetDetailsList.map((facetDetails, ind) => (
            <li
              key={facetDetails.facetUrl}
              className={classNames(styles.facetListItem, {
                [styles.facetListItem__divider!]:
                  ind === numberOfOwnBrandItems - 1,
              })}
              hidden={ind >= numberOfValuesToShow}
              data-testid="facet-list-item"
              onClick={() => {
                if (position <= 5)
                  sendMonetateEvent("plp.facets.interaction.top5");
              }}
            >
              <FacetItem
                dimensionName={facet.dimensionName}
                dispatchFacetAnalytics={dispatchFacetAnalyticsWithAnnouncement}
                facetDetails={facetDetails}
                facetName={facet.name}
                setFacetSearchValue={setFacetSearchValue}
              />
              {ind === INITIAL_NUMBER_TO_SHOW - 1 &&
                isFacetListSearchable &&
                isShowMoreOrFewerVisible &&
                isFacetListExpanded &&
                showMoreOrFewerButton()}
            </li>
          ))}
          {filteredFacetDetailsList.length === 0 && (
            <li data-testid="facet-search-no-results">
              No results were found for &apos;{facetSearchValue}&apos;. Please
              try another search.
            </li>
          )}
        </ul>
        {isShowMoreOrFewerVisible && showMoreOrFewerButton()}
      </AccordionItem>
    </Accordion>
  );
};
